import { cn } from '@/lib/utils';
import { DatePicker } from './date-picker';
import type { DatePickerBaseProps } from './types';

/**
 * SupernovaDatePicker — the app-standard date picker.
 *
 * This is the exact configuration the Reports filters use, packaged as one
 * reusable component so every page gets the same look and behaviour without
 * re-specifying the same props:
 *
 *  - **Quick presets** on (Today / Last 7 Days / This Month / …). Range modes
 *    get the range presets, single modes get the single presets — resolved
 *    automatically from `mode`.
 *  - **12-hour time** for the timed modes.
 *  - **Content-sized popover** (`w-max`, capped to the viewport) so the preset
 *    rail plus the two-month range calendar are never crushed by a narrow
 *    trigger — the bug that collapsed the weekday column headers.
 *  - **`presetFirst` (opt-in).** Pass it to open the popover onto the preset
 *    *list* — each row labelled with the dates it resolves to — instead of the
 *    calendar, with a "Custom Range…" row that swaps to the calendar. Picking a
 *    named range applies and closes in one click. Currently enabled on the two
 *    Reports pages under review (All Orders, Sales Summary for Current
 *    Location); left off elsewhere so nothing else changes until it's approved.
 *  - **Timezone handled globally.** When no `timezone` is passed, the underlying
 *    DatePicker falls through to the app reporting/tenant zone (a reactive
 *    singleton), so callers get correct tenant-timezone display, selection and
 *    formatting for free and don't need per-page timezone logic.
 *
 * It accepts every {@link DatePickerBaseProps} prop and any prop passed
 * overrides the defaults above, so it's a drop-in for any date / datetime /
 * range scenario going forward. Prefer this over importing `DatePicker`
 * directly for new pages.
 */
export function SupernovaDatePicker({
  presets = true,
  use12Hour = true,
  popoverClassName,
  ...props
}: DatePickerBaseProps) {
  return (
    <DatePicker
      {...props}
      presets={presets}
      use12Hour={use12Hour}
      // Merge so callers can still widen/constrain the popover; the defaults win
      // only for the width behaviour, and an explicit popoverClassName is appended.
      popoverClassName={cn('w-max max-w-[calc(100vw-2rem)]', popoverClassName)}
    />
  );
}
